import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  ApplyOpeningsRequest,
  BrokerRateFilters,
  BrokerRateModel,
  BrokerRateRequest,
  BrokerRatesStatisticsModel,
  CarClassModel,
  CarModelListFilters,
  CarModelModel,
  CarModelRequest,
  DeskGroupFilters,
  DeskGroupModel,
  DeskGroupRequest,
  DeskListFilters,
  DeskModel,
  DeskOpeningExceptionModel,
  DeskOpeningExceptionRequest,
  DeskOpeningModel,
  DeskOpeningRequest,
  DeskRequest,
  EditBrokerRateRequest,
  EditDeskRequest,
  ItemListFilters,
  ItemModel,
  ItemRequest,
  ProductFilters,
  ProductModel,
  ProductRequest,
  SupplierFullSyncStatus,
  SupplierInfoModel,
  SupplierRequest
} from './core.types';
import { GatewayResponse, Page, PageRequest, RevisionModel } from '../api.types';

const FRONTEND_API_URL = window.config.apiUrls.frontendServerApi;

const listAllCarModels = async (countRatePlans?: boolean, filters?: CarModelListFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<CarModelModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/car-models`,
    {
      params: {
        combinedFieldFilter: filters?.combinedFieldFilter,
        tag: filters?.tag,
        hasPrice: filters?.hasPrice,
        page: page?.page,
        size: page?.size || 500,
        countRatePlans: countRatePlans,
        sortField: page?.sortField,
        sortDirection: page?.sortDirection
      }
    }
  );
  return data;
};

const listAllAcrissCodes = async (pageNumber: number): Promise<CarClassModel[]> => {
  const { data }: AxiosResponse<GatewayResponse<Page<CarModelModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/car-models`,
    {
      params: {
        page: pageNumber,
        size: 500
      }
    }
  );

  const content: CarClassModel[] = [];
  data.data.content.forEach((carModel: CarModelModel) => {
    if (carModel.hasPrice && !content.some(carClass => carClass.acrissCode === carModel.acrissCode)) {
      content.push({ acrissCode: carModel.acrissCode, id: carModel.id, name: carModel.name });
    }
  });
  let response: CarClassModel[] = [];
  if (!data.data.last) {
    response = await listAllAcrissCodes(pageNumber + 1);
  }

  return content.concat(response);

};

const getOneCarModel = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<CarModelModel>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/car-models/${id}`
  );

  return data;
};

const getCarModelRevisions = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/car-models/${id}/revisions`
  );

  return data;
};

const addNewCarModel = async (
  request: CarModelRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<CarModelModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/car-models`,
    request
  );

  return data;
};

const editCarModel = async (
  carModelId: number,
  request: CarModelRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<CarModelModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/car-models/${carModelId}`,
    request
  );

  return data;
};

const deleteCarModel = async (carModelId: number) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/car-models/${carModelId}`);
};

const listAllDesks = async (filters?: DeskListFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<DeskModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desks`,
    {
      params: {
        combinedFieldFilter: filters ? filters.combinedFieldFilter : undefined,
        groups: filters ? filters.groups : undefined,
        statusType: filters ? filters.status : undefined,
        page: page ? page.page : undefined,
        size: page ? page.size : undefined,
        sortField: page ? page.sortField : undefined,
        sortDirection: page ? page.sortDirection : undefined
      }
    }
  );
  return data;
};

const getOneDesk = async (deskCode: string) => {
  const { data }: AxiosResponse<GatewayResponse<DeskModel>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}`
  );

  return data;
};

const addNewDesk = async (
  request: DeskRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/desks`,
    request
  );

  return data;
};

const editDesk = async (
  deskCode: string,
  request: EditDeskRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}`,
    request
  );

  return data;
};

const deleteDesk = async (deskCode: string) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}`);
};

const listAllProducts = async (filters?: ProductFilters, pageRequest?: PageRequest, isDeletedIncluded?: boolean) => {
  const { data }: AxiosResponse<GatewayResponse<Page<ProductModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/products`,
    {
      params: {
        name: filters && filters.name ? filters.name : undefined,
        page: filters && filters.pageNumber ? filters.pageNumber : undefined,
        size: pageRequest ? pageRequest.size : undefined,
        isDeletedIncluded: isDeletedIncluded,
        sortField: pageRequest ? pageRequest.sortField : undefined,
        sortDirection: pageRequest ? pageRequest.sortDirection : undefined
      }
    }
  );
  return data;
};

const getOneProduct = async (productId: number) => {
  const { data }: AxiosResponse<GatewayResponse<ProductModel>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/products/${productId}`
  );

  return data;
};

const addNewProduct = async (
  request: ProductRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<ProductModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/products`,
    request
  );

  return data;
};

const editProduct = async (
  productId: number,
  request: ProductRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<ProductModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/products/${productId}`,
    request
  );

  return data;
};

const getProductRevisions = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/products/${id}/revisions`
  );

  return data;
};

const deleteProduct = async (productId: number) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/products/${productId}`);
};


const listAllBrokerRatesAllPages = async (pageNumber: number): Promise<BrokerRateModel[]> => {
  const { data }: AxiosResponse<GatewayResponse<Page<BrokerRateModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/broker-rates`,
    {
      params: {
        page: pageNumber,
        size: 20
      }
    }
  );

  const content = data.data.content;
  let response: BrokerRateModel[] = [];
  if (!data.data.last) {
    response = await listAllBrokerRatesAllPages(pageNumber + 1);
  }

  return content.concat(response);

};
const listAllBrokerRates = async (filters?: BrokerRateFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<BrokerRateModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/broker-rates`,
    {
      params: {
        combinedFilterField: filters && filters.combinedFilterField ? filters.combinedFilterField : undefined,
        brokerId: filters && filters.brokerId ? filters.brokerId : undefined,
        productId: filters && filters.productId ? filters.productId : undefined,
        itemId: filters && filters.itemId ? filters.itemId : undefined,
        status: filters && filters.status ? filters.status : undefined,
        paymentType: filters && filters.paymentType ? filters.paymentType : undefined,
        rateType: filters && filters.rateType ? filters.rateType : undefined,
        page: page && page.page ? page.page : undefined,
        size: page && page.size ? page.size : undefined,
        sortField: page && page.sortField ? page.sortField : undefined,
        sortDirection: page && page.sortDirection ? page.sortDirection : undefined
      }
    }
  );

  return data;
};

const getBrokerRatesStatistics = async () => {
  const { data }: AxiosResponse<GatewayResponse<BrokerRatesStatisticsModel[]>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/broker-rates/statistics`
  );

  return data;
};

const getOneBrokerRate = async (code: string) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerRateModel>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/broker-rates/${code}`
  );

  return data;
};

const getBrokerRateRevisions = async (code: string) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/broker-rates/${code}/revisions`
  );

  return data;
};

const addNewBrokerRate = async (
  request: BrokerRateRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerRateModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/broker-rates`,
    request
  );

  return data;
};

const editBrokerRate = async (
  code: string,
  request: EditBrokerRateRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<BrokerRateModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/broker-rates/${code}`,
    request
  );

  return data;
};

const deleteBrokerRate = async (code: string) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/broker-rates/${code}`);
};

const listAllItems = async (filters?: ItemListFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<ItemModel>>> =
    await axios.get(`${FRONTEND_API_URL}/:supplier/setup/items`,
      {
        params: {
          combinedFilterField: filters?.combinedFilterField,
          type: filters?.type,
          page: filters?.pageNumber,
          size: page?.size,
          sortField: page?.sortField,
          sortDirection: page?.sortDirection
        }
      }
    );

  return data;
};

const listAllItemsAllPages = async (page?: number) => {
  const { data }: AxiosResponse<GatewayResponse<Page<ItemModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/items`,
    {
      params: {
        size: 50,
        page: page || 0
      }
    }
  );

  const content = data.data.content;
  let response: ItemModel[] = [];
  if (!data.data.last) response = await listAllItemsAllPages(page ? page + 1 : 1);

  return content.concat(response);
};

const getOneItem = async (itemId: number) => {
  const { data }: AxiosResponse<GatewayResponse<ItemModel>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/items/${itemId}`
  );

  return data;
};

const addNewItem = async (
  request: ItemRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<ItemModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/items`,
    request
  );

  return data;
};

const editItem = async (
  itemId: number,
  request: ItemRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<ItemModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/items/${itemId}`,
    request
  );

  return data;
};

const deleteItem = async (itemId: number) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/items/${itemId}`);
};


const listAllDeskGroups = async (filters?: DeskGroupFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<DeskGroupModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desk/group`,
    {
      params: {
        name: filters ? filters.name : undefined,
        page: page ? page.page : undefined,
        sortField: page ? page.sortField : undefined,
        sortDirection: page ? page.sortDirection : undefined
      }
    }
  );

  return data;
};

const getOneDeskGroup = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<DeskGroupModel>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desk/group/${id}`
  );

  return data;
};

const addNewDeskGroup = async (
  request: DeskGroupRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskGroupModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/desk/group`,
    request
  );

  return data;
};

const editDeskGroup = async (
  id: number,
  request: DeskGroupRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskGroupModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/desk/group/${id}`,
    request
  );

  return data;
};

const deleteDeskGroup = async (id: number) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/desk/group/${id}`);
};

const getSupplierInfo = async () => {
  const { data }: AxiosResponse<GatewayResponse<SupplierInfoModel>> = await axios.get(
    `${FRONTEND_API_URL}/setup/suppliers/:supplier`
  );

  return data;
};

const triggerSupplierFullSync = async (code: string) => {
  await axios.post(`${FRONTEND_API_URL}/setup/suppliers/${code}/full-sync`);
};

const getSupplierFullSyncStatus = async (code: string) => {
  const { data }: AxiosResponse<GatewayResponse<SupplierFullSyncStatus>> = await axios.get(
    `${FRONTEND_API_URL}/setup/suppliers/${code}/full-sync/status`,
    { errorHandle: false } as AxiosRequestConfig
  );

  return data;
};

const editSupplier = async (code: string, request: SupplierRequest) => {
  const { data }: AxiosResponse<GatewayResponse<SupplierInfoModel>> = await axios.put(
    `${FRONTEND_API_URL}/setup/suppliers/${code}`,
    request
  );

  return data;
};

const listAllDeskOpenings = async (deskCode: string, page?: number): Promise<DeskOpeningModel[]> => {
  const { data }: AxiosResponse<GatewayResponse<Page<DeskOpeningModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-openings`,
    {
      params: {
        size: 500,
        page: page || 0
      }
    }
  );

  const content = data.data.content;
  let response: DeskOpeningModel[] = [];
  if (!data.data.last) response = await listAllDeskOpenings(deskCode, page ? page + 1 : 1);

  return content.concat(response);
};

const addNewDeskOpening = async (
  request: DeskOpeningRequest,
  deskCode: string
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskOpeningModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-openings`,
    request
  );

  return data;
};

const getDeskRevisions = async (deskCode: string) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/revisions`
  );

  return data;
};

const getDeskOpeningRevisions = async (deskCode: string) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-openings/revisions`
  );

  return data;
};

const getDeskOpeningExceptionRevisions = async (deskCode: string) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-opening-exceptions/revisions`
  );

  return data;
};

const editDeskOpening = async (
  id: number,
  request: DeskOpeningRequest,
  deskCode: string
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskOpeningModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-openings/${id}`,
    request
  );

  return data;
};

const deleteDeskOpening = async (id: number, deskCode: string) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-openings/${id}`);
};

const listAllDeskOpeningExceptions = async (deskCode: string, page?: number): Promise<DeskOpeningExceptionModel[]> => {
  const { data }: AxiosResponse<GatewayResponse<Page<DeskOpeningExceptionModel>>> = await axios.get(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-opening-exceptions`,
    {
      params: {
        size: 500,
        page: page || 0
      }
    }
  );

  const content = data.data.content;
  let response: DeskOpeningExceptionModel[] = [];
  if (!data.data.last) response = await listAllDeskOpeningExceptions(deskCode, page ? page + 1 : 1);

  return content.concat(response);
};

const addNewDeskOpeningException = async (
  request: DeskOpeningExceptionRequest,
  deskCode: string
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskOpeningExceptionModel>> = await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-opening-exceptions`,
    request
  );

  return data;
};

const editDeskOpeningException = async (
  id: number,
  request: DeskOpeningExceptionRequest,
  deskCode: string
) => {
  const { data }: AxiosResponse<GatewayResponse<DeskOpeningExceptionModel>> = await axios.put(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-opening-exceptions/${id}`,
    request
  );

  return data;
};

const deleteDeskOpeningException = async (id: number, deskCode: string) => {
  await axios.delete(`${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}/desk-opening-exceptions/${id}`);
};

const applyDeskOpeningsFromOtherDesk = async (
  request: ApplyOpeningsRequest,
  deskCode: string
) => {
  await axios.post(
    `${FRONTEND_API_URL}/:supplier/setup/desks/${deskCode}`,
    request
  );
};

const api = {
  CarModels: {
    listAllCarModels,
    getOneCarModel,
    addNewCarModel,
    deleteCarModel,
    editCarModel,
    listAllAcrissCodes,
    getCarModelRevisions
  },
  Desks: {
    listAllDesks,
    getOneDesk,
    addNewDesk,
    editDesk,
    deleteDesk,
    applyDeskOpeningsFromOtherDesk,
    getDeskRevisions,
    getDeskOpeningRevisions,
    getDeskOpeningExceptionRevisions
  },
  Products: {
    listAllProducts,
    getOneProduct,
    addNewProduct,
    editProduct,
    deleteProduct,
    getProductRevisions
  },
  BrokerRates: {
    listAllBrokerRates,
    getOneBrokerRate,
    addNewBrokerRate,
    editBrokerRate,
    deleteBrokerRate,
    listAllBrokerRatesAllPages,
    getBrokerRatesStatistics,
    getBrokerRateRevisions
  },
  Items: {
    listAllItems,
    listAllItemsAllPages,
    getOneItem,
    addNewItem,
    editItem,
    deleteItem
  },
  DeskGroups: {
    listAllDeskGroups,
    getOneDeskGroup,
    addNewDeskGroup,
    editDeskGroup,
    deleteDeskGroup
  },
  SupplierInfo: {
    getSupplierInfo,
    editSupplier,
    triggerSupplierFullSync,
    getSupplierFullSyncStatus
  },
  DeskOpenings: {
    listAllDeskOpenings,
    addNewDeskOpening,
    editDeskOpening,
    deleteDeskOpening
  },
  DeskOpeningExceptions: {
    listAllDeskOpeningExceptions,
    addNewDeskOpeningException,
    editDeskOpeningException,
    deleteDeskOpeningException
  }
};

export default api;
